import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import type { AdvertiserBrand, BrandSeparationMode } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/useAuth'
import { brandService } from '@/services/brand_service'
import { SEPARATION_MODES, SEPARATION_MODE_DESCRIPTION, SEPARATION_MODE_LABEL } from './separationMode'

interface BrandDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: number
  brand: AdvertiserBrand | null
  onSaved: () => void
}

export function BrandDialog({ open, onOpenChange, supplierId, brand, onSaved }: BrandDialogProps) {
  const { token } = useAuth()
  const [name, setName] = useState('')
  const [mode, setMode] = useState<BrandSeparationMode>('none')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(brand?.name ?? '')
    setMode(brand?.separation_mode ?? 'none')
    setIsActive(brand?.is_active ?? true)
    setSaving(false)
  }, [open, brand])

  const handleSave = async () => {
    if (!token || name.trim() === '') return
    setSaving(true)
    const result = brand
      ? await brandService.update(supplierId, brand.brand_id, { name: name.trim(), separation_mode: mode, is_active: isActive }, token)
      : await brandService.create(supplierId, { name: name.trim(), separation_mode: mode }, token)
    setSaving(false)
    if (result.success) {
      toast.success(brand ? '已更新品牌' : '已新增品牌')
      onOpenChange(false)
      onSaved()
    } else {
      toast.error(result.error?.message || '儲存失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{brand ? '編輯品牌' : '新增品牌'}</DialogTitle>
          <DialogDescription>素材、Banner 圖可以掛品牌；引擎依「同品牌不相鄰播出」的設定排開。</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="brand-name">品牌名稱</Label>
            <Input id="brand-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} placeholder="例：骨質疏鬆系列" />
          </div>
          <div className="space-y-2">
            <Label>同品牌不相鄰播出</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as BrandSeparationMode)} className="gap-2">
              {SEPARATION_MODES.map((m) => (
                <label key={m} htmlFor={`brand-mode-${m}`} className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                  <RadioGroupItem id={`brand-mode-${m}`} value={m} className="mt-0.5" />
                  <span className="space-y-1">
                    <span className="block text-sm font-medium">{SEPARATION_MODE_LABEL[m]}</span>
                    <span className="block text-xs text-muted-foreground">{SEPARATION_MODE_DESCRIPTION[m]}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>
          {brand && (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">啟用</p>
                <p className="text-xs text-muted-foreground">停用後素材與 Banner 的下拉不再列出，已掛的不受影響。</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={saving || name.trim() === ''}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            儲存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
